import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useConfirm } from '../contexts/ConfirmContext';
import { getCandidates, softDelete } from '../services/firestoreService';
import { SkeletonTableRow } from '../components/ui/Skeleton';
import {
  getConferenceStatusLabel,
  getConferenceStatusStyle,
  getProcessStageLabel,
  getProcessStageStyle,
} from '../utils/labelHelpers';
import '../styles/CandidateList.css';

const PAGE_SIZE = 1000; // Load all candidates — dataset is small enough that client-side search/filter is correct

const CandidateList = () => {
  // Shared data from context — no extra fetches needed
  const { currentUser, statusConfig, districts } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();
  const [candidates, setCandidates] = useState([]);
  const [filteredCandidates, setFilteredCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [stageFilter, setStageFilter] = useState('all');
  const [districtFilter, setDistrictFilter] = useState('all');
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [sortField, setSortField] = useState('lastName');
  const [sortDirection, setSortDirection] = useState('asc');
  const [showArchived, setShowArchived] = useState(false);
  const [searchParams] = useSearchParams();

  // For dCOM roles, translate their district IDs to names for server-side filtering
  const userRole = currentUser?.role;
  const isDCOM = userRole === 'dCOM' || userRole === 'dCOM Member' || userRole === 'dCOM Chair';
  const userDistrictIds = currentUser?.districtIds || (currentUser?.districtId ? [currentUser.districtId] : []);
  const userDistrictNames = isDCOM
    ? userDistrictIds.map(id => districts.find(d => d.id === id)?.name).filter(Boolean)
    : null; // null means "no district restriction"

  const fetchCandidates = useCallback(async () => {
    // dCOM users with no resolved district names would get zero results — warn early.
    if (isDCOM && userDistrictIds.length > 0 && userDistrictNames !== null && userDistrictNames.length === 0) {
      toast.error('Your account has district IDs assigned that do not match any district in the system. Please contact an administrator to correct your district assignment.');
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const filters = { pageSize: PAGE_SIZE };
      // dCOM users are restricted to their districts server-side
      if (isDCOM) {
        filters.districtNames = userDistrictNames || [];
      }

      const { candidates: fetched } = await getCandidates(filters);
      setCandidates(fetched);
    } catch (error) {
      console.error('Error fetching candidates:', error);
      if (error?.code === 'permission-denied') {
        toast.error('Permission denied loading candidates. Your account may be missing role permissions — please contact an administrator or try logging out and back in.');
      } else {
        toast.error('Error loading candidates. Please refresh and try again.');
      }
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDCOM, JSON.stringify(userDistrictNames)]);

  useEffect(() => {
    fetchCandidates();

    // Check for status filter from URL params
    const statusParam = searchParams.get('status');
    if (statusParam) setStatusFilter(statusParam);
  }, [fetchCandidates, searchParams]);

  useEffect(() => {
    filterCandidates();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidates, searchTerm, statusFilter, stageFilter, districtFilter, sortField, sortDirection, showArchived, statusConfig]);

  const isArchivedStage = (stage) => {
    if (!statusConfig || !statusConfig.processStages) return stage === 'archived';
    const stageConfig = statusConfig.processStages.find(s => s.id === stage);
    return stageConfig ? stageConfig.archived : stage === 'archived';
  };

  const filterCandidates = () => {
    let filtered = candidates;
    // Note: dCOM district restriction is applied server-side in fetchCandidates

    // Filter by archived status
    if (!showArchived) {
      filtered = filtered.filter(candidate => {
        const currentStage = candidate.processStage || candidate.candidacyStage;
        return !isArchivedStage(currentStage);
      });
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(candidate =>
        `${candidate.firstName} ${candidate.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
        candidate.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by conference status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(candidate => 
        candidate.conferenceStatus === statusFilter || candidate.candidateType === statusFilter
      );
    }

    // Filter by process stage
    if (stageFilter !== 'all') {
      filtered = filtered.filter(candidate => 
        candidate.processStage === stageFilter || candidate.candidacyStage === stageFilter
      );
    }

    // Filter by district (only if not dCOM, since dCOM is already filtered)
    if (districtFilter !== 'all' && !isDCOM) {
      filtered = filtered.filter(candidate => 
        candidate.district === districtFilter
      );
    }

    // Sort the filtered results
    filtered.sort((a, b) => {
      let aValue = getSortValue(a, sortField);
      let bValue = getSortValue(b, sortField);
      
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredCandidates(filtered);
  };

  const getSortValue = (candidate, field) => {
    switch (field) {
      case 'name':
        return `${candidate.lastName}, ${candidate.firstName}`.toLowerCase();
      case 'firstName':
        return candidate.firstName?.toLowerCase() || '';
      case 'lastName':
        return candidate.lastName?.toLowerCase() || '';
      case 'email':
        return candidate.email?.toLowerCase() || '';
      case 'district':
        return candidate.district?.toLowerCase() || '';
      case 'conferenceStatus':
        return candidate.conferenceStatus?.toLowerCase() || candidate.candidateType?.toLowerCase() || '';
      case 'processStage':
        return candidate.processStage?.toLowerCase() || candidate.candidacyStage?.toLowerCase() || '';
      default:
        return '';
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return '↕️';
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  const handleSelectCandidate = (candidateId) => {
    setSelectedCandidates(prev => {
      if (prev.includes(candidateId)) {
        return prev.filter(id => id !== candidateId);
      } else {
        return [...prev, candidateId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedCandidates([]);
      setSelectAll(false);
    } else {
      setSelectedCandidates(filteredCandidates.map(candidate => candidate.id));
      setSelectAll(true);
    }
  };

  const clearSelection = () => {
    setSelectedCandidates([]);
    setSelectAll(false);
  };

  const handleBulkDelete = async () => {
    if (selectedCandidates.length === 0) return;

    const selectedCandidateNames = candidates
      .filter(candidate => selectedCandidates.includes(candidate.id))
      .map(candidate => `${candidate.firstName} ${candidate.middleName ? `${candidate.middleName} ` : ''}${candidate.lastName}`)
      .join(', ');

    const confirmed = await confirm({
      title: `Delete ${selectedCandidates.length} Candidate${selectedCandidates.length !== 1 ? 's' : ''}`,
      message: `${selectedCandidateNames}`,
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
      variant: 'danger',
    });

    if (confirmed) {
      try {
        // Soft-delete: marks deleted:true instead of permanently removing
        await Promise.all(
          selectedCandidates.map(candidateId =>
            softDelete('candidates', candidateId, currentUser)
          )
        );

        // Remove from local state immediately
        setCandidates(prev => prev.filter(c => !selectedCandidates.includes(c.id)));
        setSelectedCandidates([]);
        setSelectAll(false);

        toast.success(`Deleted ${selectedCandidates.length} candidate${selectedCandidates.length !== 1 ? 's' : ''}.`);
      } catch (error) {
        console.error('Error deleting candidates:', error);
        toast.error('Error deleting candidates. Please try again.');
      }
    }
  };

  const handleExportCSV = () => {
    // All candidates are already loaded (PAGE_SIZE = 1000), use the filtered list directly
    const exportCandidates = filteredCandidates;

    const escapeCSV = (value) => {
      if (value == null) return '';
      const str = String(value);
      return str.includes(',') || str.includes('"') || str.includes('\n')
        ? `"${str.replace(/"/g, '""')}"`
        : str;
    };

    const headers = ['First Name', 'Last Name', 'Email', 'Phone', 'District', 'Home Church', 'Conference Status', 'Process Stage'];
    const rows = exportCandidates.map(c => [
      escapeCSV(c.firstName),
      escapeCSV(c.lastName),
      escapeCSV(c.email),
      escapeCSV(c.phone || c.cellPhone),
      escapeCSV(c.district),
      escapeCSV(c.homeChurch || c.chargeName),
      escapeCSV(getConferenceStatusLabel(c.conferenceStatus || c.candidateType, statusConfig)),
      escapeCSV(getProcessStageLabel(c.processStage || c.candidacyStage, statusConfig)),
    ].join(','));

    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `candidates_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const getArchivedCount = () => {
    return candidates.filter(candidate => {
      const currentStage = candidate.processStage || candidate.candidacyStage;
      return isArchivedStage(currentStage);
    }).length;
  };

  if (loading) {
    return (
      <div className="candidate-list-container">
        <div className="candidate-list-header">
          <div className="header-title"><h1>Ministry Candidates</h1></div>
        </div>
        <div className="candidates-table-container">
          <div className="candidates-table">
            <table>
              <thead>
                <tr>
                  {['', 'Name', 'Email', 'District', 'Conference Status', 'Process Stage', 'Actions'].map(h => (
                    <th key={h}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: 8 }).map((_, i) => <SkeletonTableRow key={i} cols={7} />)}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="candidate-list-container">
      {isDCOM && userDistrictIds.length === 0 && (
        <div className="warning-banner">
          No district has been assigned to your account. Contact an administrator to assign your district so you can view candidates.
        </div>
      )}
      <div className="candidate-list-header">
        <div className="header-title">
          <h1>Ministry Candidates</h1>
          {isDCOM && userDistrictIds.length > 0 && (
            <div className="district-indicator">
              <span className="district-label">
                Viewing: {userDistrictIds.map(id => {
                  const district = districts.find(d => d.id === id);
                  return district ? district.name : 'Unknown';
                }).join(', ')}
              </span>
            </div>
          )}
        </div>
        <div className="header-actions">
          <button onClick={handleExportCSV} className="export-csv-btn">
            Export CSV
          </button>
          <a href="/candidates/import" className="import-candidate-btn">
            Import Candidates
          </a>
          <a href="/candidates/add" className="add-candidate-btn">
            Add New Candidate
          </a>
        </div>
      </div>

      <div className="filters-section">
        <div className="search-and-actions">
          <div className="search-box">
            <input
              type="text"
              placeholder="Search candidates by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
          <div className="quick-actions">
            <label className="archived-toggle">
              <input
                type="checkbox"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
              />
              Show Archived ({getArchivedCount()})
            </label>
          </div>
        </div>

        <div className="filter-controls">
          <div className="filter-group">
            <label className="filter-label">Conference Status:</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Status Types</option>
              {statusConfig && statusConfig.conferenceStatuses ? 
                statusConfig.conferenceStatuses
                  .filter(status => status.enabled)
                  .map(status => (
                    <option key={status.id} value={status.id}>
                      {status.label}
                    </option>
                  )) :
                <>
                  <option value="lay">Lay</option>
                  <option value="clm">Certified Lay Ministry</option>
                  <option value="cmc">Certified Ministry Candidate</option>
                  <option value="supply-pastor">Supply Pastor</option>
                  <option value="local-pastor">Local Pastor</option>
                  <option value="other-fellowship">Other Fellowship</option>
                  <option value="other">Other</option>
                </>
              }
            </select>
          </div>

          <div className="filter-group">
            <label className="filter-label">Process Stage:</label>
            <select
              value={stageFilter}
              onChange={(e) => setStageFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Stages</option>
              {statusConfig && statusConfig.processStages ? 
                statusConfig.processStages
                  .filter(stage => stage.enabled)
                  .map(stage => (
                    <option key={stage.id} value={stage.id}>
                      {stage.label}
                    </option>
                  )) :
                <>
                  <option value="inquiring">Inquiring</option>
                  <option value="certified">Certified</option>
                  <option value="full-time">Full-Time</option>
                  <option value="part-time">Part-Time</option>
                  <option value="completed">Completed</option>
                  <option value="discontinued">Discontinued</option>
                  <option value="archived">Archived</option>
                </>
              }
            </select>
          </div>

          {!isDCOM && (
            <div className="filter-group">
              <label className="filter-label">District:</label>
              <select
                value={districtFilter}
                onChange={(e) => setDistrictFilter(e.target.value)}
                className="filter-select"
              >
                <option value="all">All Districts</option>
                {districts
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map(district => (
                    <option key={district.id} value={district.name}>
                      {district.name}
                    </option>
                  ))}
              </select>
            </div>
          )}
        </div>
      </div>

      {filteredCandidates.length > 0 && (
        <div className="selection-controls">
          <div className="selection-actions">
            <label className="select-all-checkbox">
              <input
                type="checkbox"
                checked={selectAll}
                onChange={handleSelectAll}
              />
              Select All ({filteredCandidates.length})
            </label>
            {selectedCandidates.length > 0 && (
              <>
                <span className="selected-count">
                  {selectedCandidates.length} selected
                </span>
                <button onClick={handleBulkDelete} className="delete-selected-btn">
                  Delete Selected
                </button>
                <button onClick={clearSelection} className="clear-selection-btn">
                  Clear Selection
                </button>
              </>
            )}
          </div>
        </div>
      )}

      <div className="candidates-table-container">
        {filteredCandidates.length === 0 ? (
          <div className="no-candidates">
            <p>No candidates found matching your criteria.</p>
            {!showArchived && getArchivedCount() > 0 && (
              <p>
                <button 
                  onClick={() => setShowArchived(true)}
                  className="show-archived-btn"
                >
                  Show {getArchivedCount()} archived candidate{getArchivedCount() !== 1 ? 's' : ''}
                </button>
              </p>
            )}
          </div>
        ) : (
          <div className="candidates-table">
            <table>
              <thead>
                <tr>
                  <th className="checkbox-column">
                    <input
                      type="checkbox"
                      checked={selectAll}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th 
                    className="sortable-header"
                    onClick={() => handleSort('name')}
                  >
                    Name {getSortIcon('name')}
                  </th>
                  <th 
                    className="sortable-header"
                    onClick={() => handleSort('email')}
                  >
                    Email {getSortIcon('email')}
                  </th>
                  <th 
                    className="sortable-header"
                    onClick={() => handleSort('district')}
                  >
                    District {getSortIcon('district')}
                  </th>
                  <th 
                    className="sortable-header"
                    onClick={() => handleSort('conferenceStatus')}
                  >
                    Conference Status {getSortIcon('conferenceStatus')}
                  </th>
                  <th 
                    className="sortable-header"
                    onClick={() => handleSort('processStage')}
                  >
                    Process Stage {getSortIcon('processStage')}
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredCandidates.map(candidate => {
                  const currentStage = candidate.processStage || candidate.candidacyStage;
                  const isArchived = isArchivedStage(currentStage);
                  
                  return (
                    <tr 
                      key={candidate.id} 
                      className={`${selectedCandidates.includes(candidate.id) ? 'selected' : ''} ${isArchived ? 'archived-candidate' : ''}`}
                    >
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedCandidates.includes(candidate.id)}
                          onChange={() => handleSelectCandidate(candidate.id)}
                        />
                      </td>
                      <td className="candidate-name">
                        <div className="name-cell">
                          <strong>
                            {candidate.firstName} {candidate.middleName ? `${candidate.middleName} ` : ''}{candidate.lastName}
                            {isArchived && <span className="archived-indicator"> (Archived)</span>}
                          </strong>
                          {candidate.currentAppointment && (
                            <div className="appointment-info">
                              {candidate.currentAppointment}
                            </div>
                          )}
                        </div>
                      </td>
                      <td>{candidate.email || 'Not provided'}</td>
                      <td>{candidate.district || 'Not assigned'}</td>
                      <td>
                        <span 
                          className={`status-badge ${candidate.conferenceStatus || candidate.candidateType}`}
                          style={getConferenceStatusStyle(candidate.conferenceStatus || candidate.candidateType, statusConfig)}
                        >
                          {getConferenceStatusLabel(candidate.conferenceStatus || candidate.candidateType, statusConfig)}
                        </span>
                      </td>
                      <td>
                        <span 
                          className={`stage-badge ${currentStage} ${isArchived ? 'archived' : ''}`}
                          style={getProcessStageStyle(currentStage, statusConfig)}
                        >
                          {getProcessStageLabel(currentStage, statusConfig)}
                        </span>
                      </td>
                      <td>
                        <Link to={`/candidates/${candidate.id}`} className="view-btn">
                         Profile
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="candidates-summary">
        <p>
          Showing {filteredCandidates.length} of {candidates.length} candidates
          {!showArchived && getArchivedCount() > 0 && (
            <span> ({getArchivedCount()} archived)</span>
          )}
        </p>

      </div>
    </div>
  );
};

export default CandidateList;
